import React from 'react';
import { Badge } from '../ui/badge';
import { PHASES, PRE_RELEASE_WINDOW } from './releaseData';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';

function fmtNum(v, digits = 0) {
  return v == null ? '—' : Number(v).toLocaleString('en-GB', { maximumFractionDigits: digits });
}
function fmtEur(v) { return v == null ? '—' : `€${fmtNum(v)}`; }
function fmtPct(v) { return v == null ? '—' : `${Number(v).toFixed(2)}%`; }
function fmtShare(v) { return v == null ? '—' : `${(100 * v).toFixed(0)}%`; }

function SummaryRow({ s }) {
  return (
    <TableRow>
      <TableCell className="whitespace-nowrap">
        <Badge variant={s.variant} className="whitespace-nowrap">{s.label}</Badge>
      </TableCell>
      <TableCell className="text-right">{fmtNum(s.games)}</TableCell>
      <TableCell className="text-right text-ink-dim">{s.pending === 0 ? '' : fmtNum(s.pending)}</TableCell>
      <TableCell className="text-right">{fmtNum(s.avg_days, 1)}</TableCell>
      <TableCell className="text-right">{fmtNum(s.avg_players)}</TableCell>
      <TableCell className="text-right">{fmtNum(s.median_players)}</TableCell>
      <TableCell className="text-right">{fmtShare(s.vip_share)}</TableCell>
      <TableCell className="text-right">{fmtEur(s.avg_turnover_eur)}</TableCell>
      <TableCell className="text-right">{fmtEur(s.median_turnover_eur)}</TableCell>
      <TableCell className="text-right">{fmtEur(s.avg_turnover_per_day)}</TableCell>
      <TableCell className="text-right">{fmtEur(s.avg_ggr_eur)}</TableCell>
      <TableCell className="text-right">{fmtPct(s.payout_pct)}</TableCell>
    </TableRow>
  );
}

// Side-by-side launch performance per release type for the currently
// filtered games — e.g. narrow to one provider to compare its pre-releases
// against its standard releases.
export default function ReleaseTypeSummary({ summary, windowKey, phase }) {
  const prePeriod = windowKey === PRE_RELEASE_WINDOW;
  if (summary.length === 0) return null;
  const pendingTotal = summary.reduce((s, r) => s + r.pending, 0);

  return (
    <div className="mb-6">
      <p className="text-[11px] font-bold uppercase tracking-wide text-ink-dim mb-2">
        {prePeriod
          ? `Pre-release period${phase === 'combined' ? '' : ` — ${PHASES.find(p => p.key === phase).label}`}`
          : `First ${windowKey} days after release`}, by release type
      </p>
      <div className="border border-white/10 rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Release type</TableHead>
              <TableHead className="text-right">Games compared</TableHead>
              <TableHead className="text-right">Not in summary</TableHead>
              <TableHead className="text-right">Avg days</TableHead>
              <TableHead className="text-right">Avg players</TableHead>
              <TableHead className="text-right">Median players</TableHead>
              <TableHead className="text-right whitespace-nowrap">VIP share</TableHead>
              <TableHead className="text-right">Avg turnover</TableHead>
              <TableHead className="text-right">Median turnover</TableHead>
              <TableHead className="text-right">Avg turnover / day</TableHead>
              <TableHead className="text-right">Avg GGR</TableHead>
              <TableHead className="text-right">Payout</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summary.map(s => <SummaryRow key={s.key} s={s} />)}
          </TableBody>
        </Table>
      </div>
      <p className="text-[11px] text-ink-dim mt-2">
        {prePeriod
          ? 'Each game is measured from its pre-release start up to its general release day. Periods differ in length, so compare turnover per day too. Games that weren\'t pre-released are measured over their first 14 days after release instead, about a typical pre-release\'s length.'
          : `Each game is measured over its first ${windowKey} days after going live.`}
        {' '}Only finished periods of games that were actually played are averaged{pendingTotal > 0 ? ` — ${pendingTotal} game${pendingTotal === 1 ? ' is' : 's are'} left out: still running, or no players at all (probably never live); see "Not in summary" below` : ''}.
        {phase === 'vip' && ' Only pre-releases with a VIP phase have a VIP-only window; a VIP week is usually half a pre-release, so compare turnover per day.'}
        {phase === 'open' && ' A pre-release without a VIP phase is open for its whole period; one that was VIP-only throughout has no open part and shows —.'}
        {' '}VIP share is the share of players flagged VIP today, which may differ from their status when they played.
        {' '}Untracked games were released before release tracking began, so some of them may have been pre-releases.
      </p>
    </div>
  );
}
